/**
 * useShellPings - speech aimed at the map, and the log lines that point back at it.
 *
 * The SENDER works out what a ping landed on, so everyone reads the same
 * sentence off it and it cannot go stale: `inspectAt` already answers "what
 * is here" for structures and painted ground, and the distance from the
 * person pointing is the question "danger, over there" is really answering.
 *
 * THE RING IS A GESTURE; THE LOG LINE IS THE RECORD - half a second is too
 * short to read twice, and the entry is what somebody looking at their sheet
 * can come back to and click, to send their own camera there. A climb
 * (the map only reports one over a stride) is written the same way.
 *
 * `mapFocus` is a counter, not just a point: clicking the same entry twice
 * must bring the camera back the second time. Returns `{ pings, mapFocus,
 * lookAtOnMap, handlePing, handleMapClimb, handleMapMove }`. Extracted from DeathWatchSimulator.
 */
import { useState, useCallback } from 'react';
import * as multiplayer from '../multiplayer';
import { inspectAt } from '../engine/battlefield/inspect';
import { formatMetres, rangeBandFor } from '../engine/battlefield/measure';
import { PING_TTL_MS, PING_TYPES } from '../engine/battlefield/pings';
import { RANGE_LABELS } from '../engine/attackCalc';
import usePings from './usePings';
import { cellSizeOf } from './useShellLiveMap';
import { moveEntry } from '../engine/moveBeat';

/** The NAME and the one fact that matters in a fight - not the whole readout. */
function whatIsThere(ask, field) {
  const lines = inspectAt(
    { x: ask.x, y: 0, z: ask.z },
    {
      solids: field.mapSolids,
      zones: field.terrain?.zones,
      cellSize: cellSizeOf(field.terrain),
      includeHidden: field.isGm,
    },
  );
  if (!lines.length) return null;
  const found = lines[0];
  return [found.title, found.meaning[0] || found.facts[0]].filter(Boolean).join(' · ');
}

/** How far it is from the person pointing, and in which band of their weapon. */
function howFar({ x, y, z }, me, weaponRange) {
  const metres = Math.hypot(x - me.x, (y ?? 0) - (me.elevation ?? 0), z - me.z);
  const band = rangeBandFor(metres, weaponRange);
  return `${formatMetres(metres)} away${band ? ` · ${RANGE_LABELS[band] || band}` : ''}`;
}

/**
 * The sentence a ping carries. `describeAt`, when the press was on a
 * structure: the ring belongs under the cursor, but "what is here" has to
 * be asked of the thing that was pressed.
 */
export function describePingText(point, field) {
  const parts = [];
  const there = whatIsThere(point.describeAt || { x: point.x, z: point.z }, field);
  if (there) parts.push(there);
  const me = field.actorId ? field.tokens[field.actorId] : null;
  if (me) parts.push(howFar(point, me, field.weaponRange));
  return parts.join(' - ');
}

/** A ping's log line: clickable, back to where it was. */
export function pingEntry(ping) {
  return {
    type: 'info',
    text: `📍 ${ping.byName}: ${PING_TYPES[ping.type].label}${ping.note ? ` - ${ping.note}` : ''}`,
    timestamp: ping.at,
    pingX: ping.x,
    pingZ: ping.z,
  };
}

/** A climb's log line, carrying the destination when the map gave one. */
export function climbEntry({ name, metres, up, spent, rung, x, z }) {
  const how = up ? 'climbed' : 'climbed down';
  const cost = spent > 0 ? ` - ${spent.toFixed(1)} m of movement` : ' - no movement spent';
  return {
    type: 'info',
    text: `🧗 ${name} ${how} ${metres.toFixed(1)} m${cost}.`,
    timestamp: Date.now(),
    title: rung,
    ...(x !== undefined && z !== undefined ? { pingX: x, pingZ: z } : {}),
  };
}

/** Sent after it is already on screen, and tidied up once nothing can draw it. */
function useSendPing(sessionId) {
  return useCallback(
    (ping) => {
      if (!sessionId) return;
      multiplayer.setBattlefieldPing(sessionId, ping).catch(console.error);
      setTimeout(() => multiplayer.deleteBattlefieldPing(sessionId, ping.id).catch(() => {}), PING_TTL_MS + 2000);
    },
    [sessionId],
  );
}

/** The ping channel, describing from the map as it stands. */
function usePingChannel({ mapSolids, terrain, isGm, actorId, tokens, weaponRange, playerId, displayName, sessionId }) {
  const describe = useCallback(
    (point) => describePingText(point, { mapSolids, terrain, isGm, actorId, tokens, weaponRange }),
    [mapSolids, terrain, isGm, actorId, tokens, weaponRange],
  );
  return usePings({ playerId, playerName: displayName, describe, onSend: useSendPing(sessionId) });
}

/**
 * @param {object} args - mapSolids, terrain, isGm, actorId, tokens,
 *   weaponRange (the armed weapon's), playerId, displayName, sessionId,
 *   activeTab, addNarrativeRef, flushNarrativeQueue and mergePingsRef (late-bound: the log and
 *   the battlefield sync are built on either side of this).
 */
export default function useShellPings(args) {
  const { actorId, activeTab, addNarrativeRef, flushNarrativeQueue, mergePingsRef } = args;
  const { pings, sendPing, mergePings } = usePingChannel(args);
  mergePingsRef.current = mergePings;
  const [mapFocus, setMapFocus] = useState(null);
  const lookAtOnMap = useCallback((x, z) => setMapFocus((prev) => ({ x, z, n: (prev?.n || 0) + 1 })), []);
  const handlePing = useCallback(
    ({ type, x, y, z, describeAt }) => {
      const ping = sendPing({ type, x, y, z, describeAt, ...(type === 'going' && actorId ? { fromId: actorId } : {}) });
      if (ping) addNarrativeRef.current?.(activeTab, pingEntry(ping));
    },
    [sendPing, actorId, activeTab, addNarrativeRef],
  );
  const handleMapClimb = useCallback(
    (climb) => addNarrativeRef.current?.(activeTab, climbEntry(climb)),
    [activeTab, addNarrativeRef],
  );
  // THE MOVE'S LINE (plan design-gaps, M2): one public line per move across the ground, its route in the beat
  // column, stamped here - the walk every screen plays reads it (engine/moveBeat, engine/display/walk).
  // The line goes out AT ONCE, ahead of the token's own write (putDown): every other screen reads the walk off
  // the line, and a token that arrived first would stand at its destination until the line caught up.
  const handleMapMove = useCallback(
    (facts) => {
      // A charge stamps its line with the run's start, so its undo can find that one entry (useChargeRun).
      addNarrativeRef.current?.(activeTab, moveEntry(facts.name, { ...facts, at: facts.at ?? Date.now() }));
      flushNarrativeQueue?.();
    },
    [activeTab, addNarrativeRef, flushNarrativeQueue],
  );
  return { pings, mapFocus, lookAtOnMap, handlePing, handleMapClimb, handleMapMove };
}
